import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  useApi,
  storageApiRef,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import {
  WHATS_NEW_SETTING_KEY,
  WHATS_NEW_MANUAL_TRIGGER_KEY,
  WHATS_NEW_AUTO_POPUP_DISABLED_KEY,
} from '../constants/userSettingsKeys';
import {
  WhatsNewRelease,
  MajorReleaseGroup,
  compareVersions,
  getMajorVersion,
  groupReleasesByMajor,
} from '../components/whatsnew-dialogue/githubReleasesService';

export const useWhatsNew = () => {
  const storageApi = useApi(storageApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);

  const userSettingsApi = useMemo(
    () => storageApi.forBucket('user-settings'),
    [storageApi],
  );

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [releases, setReleases] = useState<WhatsNewRelease[]>([]);
  const [majorGroups, setMajorGroups] = useState<MajorReleaseGroup[]>([]);
  const [latestRelease, setLatestRelease] = useState<WhatsNewRelease | null>(
    null,
  );
  const [lastSeenVersion, setLastSeenVersion] = useState<string | null>(null);
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [hasNewMajorVersion, setHasNewMajorVersion] = useState(false);
  const [autoPopupDisabled, setAutoPopupDisabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [storageReady, setStorageReady] = useState(false);
  const [autoPopupPreferenceReady, setAutoPopupPreferenceReady] =
    useState(false);

  // Use ref to track if we've already auto-opened to prevent multiple triggers
  const hasAutoOpenedRef = useRef(false);

  // Fetch all releases from backend API. Pass silent=true to refresh data
  // without flipping the global loading state (avoids the modal flashing).
  const loadReleases = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        setError(null);

        // Use discoveryApi for proper service discovery
        const baseUrl = await discoveryApi.getBaseUrl('whats-new');
        const response = await fetch(`${baseUrl}/`);

        if (!response.ok) {
          throw new Error(`Failed to fetch releases: ${response.status}`);
        }

        const data = await response.json();

        if (data.releases && data.releases.length > 0) {
          // Convert the cached releases to WhatsNewRelease format
          const transformedReleases: WhatsNewRelease[] = data.releases.map(
            (release: WhatsNewRelease & { publishedAt: string | Date }) => ({
              ...release,
              publishedAt: new Date(release.publishedAt),
            }),
          );
          setReleases(transformedReleases);
          setLatestRelease(transformedReleases[0]);

          // Group releases by major version
          const groups = groupReleasesByMajor(transformedReleases);
          setMajorGroups(groups);
        } else {
          // Handle empty response explicitly
          setReleases([]);
          setMajorGroups([]);
          setLatestRelease(null);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch releases',
        );
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [discoveryApi, fetch],
  );

  // Public fetch used on mount and manual refresh (always shows loading).
  const fetchReleases = useCallback(() => loadReleases(false), [loadReleases]);

  // Determine admin membership for showing the summary editor controls.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const adminGroup =
          configApi.getOptionalString('app.admin.group') ??
          'group:default/backstage-admins';
        if (mounted) {
          setIsAdmin((identity.ownershipEntityRefs ?? []).includes(adminGroup));
        }
      } catch {
        if (mounted) setIsAdmin(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [identityApi, configApi]);

  // Generate a draft AI summary for a release (admin only).
  const generateSummary = useCallback(
    async (version: string): Promise<string> => {
      const baseUrl = await discoveryApi.getBaseUrl('whats-new');
      const response = await fetch(
        `${baseUrl}/${encodeURIComponent(version)}/ai-summary`,
        { method: 'POST' },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          body.error || `Failed to generate summary (${response.status})`,
        );
      }
      const data = await response.json();
      return data.summary as string;
    },
    [discoveryApi, fetch],
  );

  // Save curated title + summary for a release (admin only).
  const saveAnnotation = useCallback(
    async (
      version: string,
      input: {
        title: string | null;
        summary: string | null;
        status: 'draft' | 'published';
      },
    ): Promise<void> => {
      const baseUrl = await discoveryApi.getBaseUrl('whats-new');
      const response = await fetch(
        `${baseUrl}/${encodeURIComponent(version)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Failed to save (${response.status})`);
      }
      // Silent refresh so the modal doesn't flash to the loading state.
      await loadReleases(true);
    },
    [discoveryApi, fetch, loadReleases],
  );

  // Fetch the latest releases from GitHub on demand (admin only), then reload.
  const fetchFromGithub = useCallback(async (): Promise<number> => {
    const baseUrl = await discoveryApi.getBaseUrl('whats-new');
    const response = await fetch(`${baseUrl}/refresh`, { method: 'POST' });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to fetch (${response.status})`);
    }
    const data = await response.json();
    await loadReleases(true);
    return data.count ?? 0;
  }, [discoveryApi, fetch, loadReleases]);

  // Check if there's a new version and AUTO-TRIGGER dialog for major releases only
  useEffect(() => {
    // Wait for storage, release data, and auto-popup preference to all be ready
    if (!latestRelease || !storageReady || !autoPopupPreferenceReady) {
      return;
    }

    const lastSeen = lastSeenVersion || '0.0.0';
    const isNewVersion = compareVersions(latestRelease.version, lastSeen) > 0;
    setHasNewVersion(isNewVersion);

    // Check if the new version is a major version bump (the major-version group changed)
    const latestMajor = getMajorVersion(latestRelease.version);
    const lastSeenMajor = getMajorVersion(lastSeen);
    const isMajorBump = isNewVersion && latestMajor !== lastSeenMajor;
    setHasNewMajorVersion(isMajorBump);

    // Auto-open on a major bump (legacy behavior) OR whenever the latest release
    // is an admin-gated one that's new to the viewer — gated releases are
    // explicitly published announcements, so any new one should pop up.
    const shouldAutoOpen =
      isNewVersion && (isMajorBump || Boolean(latestRelease.gated));
    if (shouldAutoOpen && !hasAutoOpenedRef.current && !autoPopupDisabled) {
      hasAutoOpenedRef.current = true;
      setOpen(true);
    }
  }, [
    latestRelease,
    lastSeenVersion,
    storageReady,
    autoPopupDisabled,
    autoPopupPreferenceReady,
  ]);

  // Fetch releases on mount
  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  // Get last seen version from storage using observable (handles async loading)
  useEffect(() => {
    let mounted = true;

    // Use observe$ to properly wait for storage to be ready
    const versionSub = userSettingsApi
      .observe$<string>(WHATS_NEW_SETTING_KEY)
      .subscribe(snap => {
        if (!mounted) return;

        if (snap.presence === 'present' && snap.value) {
          setLastSeenVersion(snap.value);
        } else if (snap.presence === 'absent') {
          setLastSeenVersion('0.0.0');
        }
        // Only set ready when we get a definitive answer (not 'unknown')
        if (snap.presence !== 'unknown') {
          setStorageReady(true);
        }
      });

    // Listen for manual trigger
    const triggerSub = userSettingsApi
      .observe$<boolean>(WHATS_NEW_MANUAL_TRIGGER_KEY)
      .subscribe(snap => {
        if (!mounted) return;
        if (snap.presence === 'present' && snap.value === true) {
          setOpen(true);
          userSettingsApi.set(WHATS_NEW_MANUAL_TRIGGER_KEY, false);
        }
      });

    // Listen for auto-popup disabled preference
    const autoPopupSub = userSettingsApi
      .observe$<boolean>(WHATS_NEW_AUTO_POPUP_DISABLED_KEY)
      .subscribe(snap => {
        if (!mounted) return;
        if (snap.presence === 'present') {
          setAutoPopupDisabled(!!snap.value);
          setAutoPopupPreferenceReady(true);
        } else if (snap.presence === 'absent') {
          setAutoPopupDisabled(false);
          setAutoPopupPreferenceReady(true);
        }
      });

    return () => {
      mounted = false;
      versionSub.unsubscribe();
      triggerSub.unsubscribe();
      autoPopupSub.unsubscribe();
    };
  }, [userSettingsApi]);

  const close = async (markAsRead: boolean) => {
    if (markAsRead && latestRelease) {
      await userSettingsApi.set(WHATS_NEW_SETTING_KEY, latestRelease.version);
      setLastSeenVersion(latestRelease.version);
      setHasNewVersion(false);
      setHasNewMajorVersion(false);
    }
    setOpen(false);
  };

  const triggerOpen = async () => {
    await userSettingsApi.set(WHATS_NEW_MANUAL_TRIGGER_KEY, true);
  };

  const setAutoPopupPreference = async (disabled: boolean) => {
    await userSettingsApi.set(WHATS_NEW_AUTO_POPUP_DISABLED_KEY, disabled);
    setAutoPopupDisabled(disabled);
  };

  return {
    open,
    loading,
    close,
    triggerOpen,
    releases,
    majorGroups,
    latestRelease,
    hasNewVersion,
    hasNewMajorVersion,
    lastSeenVersion,
    autoPopupDisabled,
    setAutoPopupPreference,
    refresh: fetchReleases,
    error,
    isAdmin,
    generateSummary,
    saveAnnotation,
    fetchFromGithub,
  };
};
